import { useMutation, useQueryClient } from "@tanstack/react-query";
import { GlobalActionsConfigRepository } from "../../domain/interfaces/GlobalActionsConfigRepository";
import { GlobalActionsConfigEntity } from "../../domain/globalActionConfig.entity";
import { globalActionsQueryKeys } from "../../infrastructure/cache/GlobalActionsCacheStrategy";
import { useOrchestratorCache } from "../../../shared/hooks/useOrchestratorCache";

export function useGlobalActionsConfigMutations(
  propertyTicker: string,
  repository: GlobalActionsConfigRepository
) {
  const queryClient = useQueryClient();
  const { regenCache } = useOrchestratorCache();

  const onSuccess = async () => {
    await queryClient.invalidateQueries({ queryKey: globalActionsQueryKeys.list(propertyTicker) });
    await regenCache("globalActions");
  };

  const createConfig = useMutation({
    mutationFn: (entity: GlobalActionsConfigEntity) => repository.create(propertyTicker, entity),
    onSuccess,
  });

  const updateConfig = useMutation({
    mutationFn: (entity: GlobalActionsConfigEntity) => repository.update(propertyTicker, entity),
    onSuccess,
  });

  const deleteConfig = useMutation({
    mutationFn: (id: string) => repository.delete(propertyTicker, id),
    onSuccess,
  });

  const reorderConfig = useMutation({
    mutationFn: ({ id, after }: { id: string; after: string }) =>
      repository.reorder(propertyTicker, id, after),
    onSuccess,
  });

  return {
    createConfig: createConfig.mutateAsync,
    updateConfig: updateConfig.mutateAsync,
    deleteConfig: deleteConfig.mutateAsync,
    reorderConfig: reorderConfig.mutateAsync,
  };
}
